import React, { useState, useEffect } from 'react';
import { 
  X, Download, Printer, Calendar, RefreshCw, FileText, Filter, Clock
} from 'lucide-react';
import '../styles/ReportsForm.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const DayBook = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    StartDate: new Date().toISOString().slice(0, 10),
    EndDate: new Date().toISOString().slice(0, 10),
    VoucherType: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState(null);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const handleGenerateReport = async () => {
    if (!filters.StartDate || !filters.EndDate) {
      setError('Please select date range');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/DayBook.php', { params: filters });
      
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleExportCSV = () => {
    if (!reportData || !reportData.vouchers || reportData.vouchers.length === 0) {
      alert('No data to export');
      return;
    }
    
    let rows = [];
    
    reportData.vouchers.forEach(voucher => {
      voucher.details.forEach((detail, index) => {
        rows.push([
          voucher.TransDate || '',
          voucher.VoucherNo || '',
          voucher.VoucherType || '',
          detail.ACHeadName || '',
          detail.Debit || '0.00',
          detail.Credit || '0.00',
          index === 0 ? (voucher.Remarks || '') : ''
        ]);
      });
      rows.push(['', '', '', '', '', '', '']); // Empty row between vouchers
    });
    
    const headers = ['Date', 'Voucher No', 'Type', 'Account Head', 'Debit', 'Credit', 'Remarks'];
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `day_book_${filters.StartDate}_to_${filters.EndDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleClearFilters = () => {
    setFilters({
      StartDate: new Date().toISOString().slice(0, 10),
      EndDate: new Date().toISOString().slice(0, 10),
      VoucherType: ''
    });
    setReportData(null);
  };
  
  if (!session?.companyid) {
    return (
      <div className="report-container">
        <div className="report-header">
          <div className="header-left">
            <h2>Session Required</h2>
            <p className="subtitle">Please select company and year</p>
          </div>
        </div>
        <div className="report-display">
          <div className="report-empty">
            <div className="empty-icon">🔒</div>
            <h3 className="empty-title">Session Required</h3>
            <p className="empty-message">Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2>Day Book</h2>
          <p className="subtitle">Daily Transaction Register</p>
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="report-toolbar no-print">
        <button onClick={handleGenerateReport} disabled={loading} className="btn-generate">
          <RefreshCw size={18} />
          Generate Report
        </button>
        <button 
          onClick={handlePrint} 
          disabled={!reportData || loading}
          className="btn-print"
        >
          <Printer size={18} />
          Print
        </button>
        <button 
          onClick={handleExportCSV} 
          disabled={!reportData || loading}
          className="btn-export"
        >
          <Download size={18} />
          Export CSV
        </button>
        <button onClick={handleClearFilters} disabled={loading} className="btn-clear">
          <RefreshCw size={18} />
          Clear
        </button>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="report-alert alert-error">
          <div className="alert-icon">⚠</div>
          <div><strong>Error:</strong> {error}</div>
        </div>
      )}
      
      {/* Filters */}
      <div className="filters-section no-print">
        <h3 className="section-title">
          <Filter size={18} />
          Report Filters
        </h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Start Date <span className="required">*</span></label>
            <input
              type="date"
              name="StartDate"
              value={filters.StartDate}
              onChange={handleFilterChange}
              required
            />
          </div>
          
          <div className="filter-group">
            <label>End Date <span className="required">*</span></label>
            <input
              type="date"
              name="EndDate"
              value={filters.EndDate}
              onChange={handleFilterChange}
              required
            />
          </div>
          
          <div className="filter-group">
            <label>Voucher Type</label>
            <select
              name="VoucherType"
              value={filters.VoucherType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="CV">CV - Cash Payment</option>
              <option value="CR">CR - Cash Receipt</option>
              <option value="BV">BV - Bank Payment</option>
              <option value="BR">BR - Bank Receipt</option>
              <option value="JV">JV - Journal Voucher</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Generating day book...</p>
        </div>
      )}
      
      {/* Report Display */}
      {reportData && !loading && (
        <div className="report-display">
          {/* Report Header */}
          <div className="report-title-section">
            <h1 className="report-company-name">
              {reportData.company?.CompanyName || 'Company Name'}
            </h1>
            <h2 className="report-name">Day Book</h2>
            <div className="report-metadata">
              <div className="report-metadata-item">
                <Calendar size={16} />
                <span>Period: {new Date(filters.StartDate).toLocaleDateString()} to {new Date(filters.EndDate).toLocaleDateString()}</span>
              </div>
              <div className="report-metadata-item">
                <Clock size={16} />
                <span>Generated: {new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {/* Vouchers */}
          {reportData.vouchers && reportData.vouchers.length > 0 ? (
            <>
              {reportData.vouchers.map((voucher, vIndex) => (
                <div key={vIndex} className="voucher-card">
                  {/* Voucher Header */}
                  <div className="voucher-card-header">
                    <div className="voucher-card-title">
                      <span className="voucher-no">{voucher.VoucherNo}</span>
                      <span className={`voucher-badge ${voucher.VoucherType.toLowerCase()}`}>
                        {voucher.VoucherType}
                      </span>
                      <span className="voucher-date">
                        {new Date(voucher.TransDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Voucher Details */}
                  <div className="voucher-card-body">
                    <table className="report-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th>Account Head</th>
                          <th className="text-right" style={{ width: '150px' }}>Debit</th>
                          <th className="text-right" style={{ width: '150px' }}>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voucher.details.map((detail, dIndex) => (
                          <tr key={dIndex}>
                            <td>
                              <span className="account-name">{detail.ACHeadName}</span>
                              {detail.Remarks && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                                  {detail.Remarks}
                                </div>
                              )}
                            </td>
                            <td className="text-right">
                              {parseFloat(detail.Debit || 0) > 0 ? (
                                <span className="amount-debit font-bold">
                                  {parseFloat(detail.Debit).toFixed(2)}
                                </span>
                              ) : (
                                <span style={{ color: '#d1d5db' }}>-</span>
                              )}
                            </td>
                            <td className="text-right">
                              {parseFloat(detail.Credit || 0) > 0 ? (
                                <span className="amount-credit font-bold">
                                  {parseFloat(detail.Credit).toFixed(2)}
                                </span>
                              ) : (
                                <span style={{ color: '#d1d5db' }}>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f9fafb', fontWeight: '700', fontSize: '15px' }}>
                          <td className="total-label">Total:</td>
                          <td className="text-right total-amount amount-debit">
                            {parseFloat(voucher.TotalDebit || 0).toFixed(2)}
                          </td>
                          <td className="text-right total-amount amount-credit">
                            {parseFloat(voucher.TotalCredit || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Voucher Remarks */}
                  {voucher.Remarks && (
                    <div className="voucher-card-footer">
                      <p className="voucher-remarks">
                        <strong>Remarks:</strong> {voucher.Remarks}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div className="report-empty">
              <div className="empty-icon">📅</div>
              <h3 className="empty-title">No Transactions Found</h3>
              <p className="empty-message">No transactions found for the selected period</p>
            </div>
          )}
          
          {/* Summary */}
          {reportData.summary && (
            <div className="summary-cards" style={{ marginTop: '32px' }}>
              <div className="summary-card blue">
                <div className="summary-card-label">Total Vouchers</div>
                <div className="summary-card-value">{reportData.summary.TotalVouchers || 0}</div>
              </div>
              
              <div className="summary-card green">
                <div className="summary-card-label">Total Debit</div>
                <div className="summary-card-value">
                  {parseFloat(reportData.summary.TotalDebit || 0).toFixed(2)}
                </div>
              </div>
              
              <div className="summary-card red">
                <div className="summary-card-label">Total Credit</div>
                <div className="summary-card-value">
                  {parseFloat(reportData.summary.TotalCredit || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
          
          {/* Signature Section */}
          <div className="signature-section print-only">
            <div className="signature-box">
              <div className="signature-line">Prepared By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Checked By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Approved By</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayBook;